/**
 * The one place the visual language lives.
 *
 * Lifted from the approved reference screens in docs/design/approved/ so that a component
 * cannot quietly invent a colour. If a value here changes, the visual gate notices.
 */

export const C = {
  ink: '#0F1B1E',
  muted: '#93A3A8',
  body: '#5B6B70',
  border: '#E5E9EC',
  borderStrong: '#D8DEE1',
  surface: '#FFFFFF',
  surfaceAlt: '#FCFDFD',
  surfaceSunk: '#F4F6F7',

  teal: '#0D6E63',
  tealSoft: '#E4F1EC',

  amberRow: '#FFFDF8',
  amberChip: '#FBEBD8',
  amberInk: '#8A5A12',
  amberBorder: '#F0E2CC',

  redChip: '#FBE4E4',
  redInk: '#9B2C2C',
  /*
    A row tint and border for something that is wrong, built to the same recipe as the amber pair
    above: barely off white, so one bad row reads at a glance down a column of clean ones without
    turning the screen into a warning.

    Two things arrived at this colour independently — a Today rule that cannot fire, and a lead
    carrying a Scam Alert — which is the argument for it being one token rather than two.
  */
  redRow: '#FFF9F9',
  redBorder: '#EFD2D2',

  slateChip: '#EEF2F4',
}

/**
 * The four steps of the automation builder, each with its own colour.
 *
 * WHY COLOUR AT ALL. The builder was four identical white panels, and identical panels have no
 * order — the eye is given no reason to start at the top. Every operations tool that does this well
 * (Enso Connect, Zapier, HubSpot) colours the trigger, the conditions and the actions differently,
 * so the shape of a rule is legible before a single word is read. The fourth, Schedule, is ours.
 *
 * They are tints of colours already in this palette rather than four new hues: the blue of the INQUIRY
 * chip, the amber of a warning, the teal of the product, the violet of Quote Sent.
 */
export const STEP = {
  trigger: { tint: '#F2F6FC', line: '#2C4C74', chip: '#DCE7F5' },
  condition: { tint: '#FFFCF6', line: '#8A5A12', chip: '#F6E9D5' },
  action: { tint: '#F1F8F5', line: '#0D6E63', chip: '#D9EDE4' },
  schedule: { tint: '#F7F4FC', line: '#5A3E8C', chip: '#E8E0F5' },
}

// THE FAMILY NAME IS 'Public Sans Variable', and it is first on purpose.
//
// That is the name @fontsource-variable/public-sans registers. The plain
// "Public Sans" behind it is not decoration: it is what a machine with the font
// already installed would use, and it keeps this list honest about the order.
//
// Until 2026-09-19 nothing loaded either of them. index.html asked Google Fonts
// for four static weights; Storybook — which is what the 31 baselines
// photograph — asked for nothing at all, so every baseline was rendered in
// whatever sans-serif the runner happened to have. The font is bundled now, in
// both, so a screenshot is of the product rather than of the machine.
export const FONT =
  '"Public Sans Variable","Public Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif'

/**
 * Stage chip colours. CONFIRMED is the only stage that gets the teal fill: it is the one state
 * worth celebrating, and giving several stages a colour would make none of them mean anything.
 */
export const STAGE_CHIP = {
  /* One chip for one stage since #554 step C: New's blue, because an enquiry is where it starts. */
  INQUIRY: { bg: '#E7EEF7', fg: '#2C4C74' },
  QUOTE_SENT: { bg: '#EDE7F6', fg: '#5A3E8C' },
  CONTRACT_PENDING: { bg: C.amberChip, fg: C.amberInk },
  CONFIRMED: { bg: C.tealSoft, fg: C.teal },
  // Checked out is Won that has finished happening, so it is the same teal gone quiet rather
  // than a new colour. Cancelled is a booking that stopped: warm enough to be noticed next to
  // Checked out, never the red Not won wears, because nobody did anything wrong.
  CHECKED_OUT: { bg: '#E8EDEB', fg: '#50625C' },
  CANCELLED: { bg: '#FBEBE4', fg: '#95502C' },
  NOT_WON: { bg: '#F0F2F3', fg: '#7C888D' },
}

export const SOURCE_CHIP = {
  Zillow: { bg: '#E7EEF7', fg: '#2C4C74' },
  'Furnished Finder': { bg: '#F3EEE7', fg: '#7A5A2E' },
  Email: { bg: C.slateChip, fg: C.body },
}

export const money = (n) =>
  n == null ? null : `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

/**
 * A DATE AND A MOMENT ARE NOT THE SAME THING, AND THIS PRODUCT RENDERED THEM THE SAME WAY.
 *
 * `2026-11-01` is a calendar date. It is the day she moves in, and it is that day in Seattle, in
 * Taipei and on the runner in Azure. Shifting it by a time zone is how a stay that starts on the
 * 1st prints as the 31st — this company has repaired 354 bookings that were wrong for that reason,
 * so `shortDate` parses and prints in UTC and must keep doing so.
 *
 * `2026-09-26T00:20:11Z` is a MOMENT. It is 5:20 in the evening of the 25th in the office where
 * everybody who reads this screen sits. Printing it in UTC put tomorrow's date on an enquiry that
 * arrived this afternoon, which is not a rounding error — it is the timeline disagreeing with the
 * person's own memory of their day.
 *
 * So there are two sets of functions below and choosing between them is the whole job: a stay, a
 * due date and a calendar day use `shortDate`; anything that HAPPENED — a message arriving, a
 * quote being sent, a stage being entered — uses the moment functions.
 *
 * WHY A NAMED ZONE RATHER THAN THE BROWSER'S. The browser's zone is a different answer on a
 * laptop in Taipei and on the machine that takes the screenshots, which is why this was UTC in the
 * first place. A named zone is one answer everywhere, keeps the tests deterministic, and follows
 * daylight saving by itself.
 */
export const COMPANY_TZ = 'America/Los_Angeles'

/** Renders a date as "Sep 12". Returns null rather than "Invalid Date" — never a fake value. */
export const shortDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/** The day a moment happened, in the office: "Sep 25". */
export const momentDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: COMPANY_TZ })
}

/** The clock on the wall when it happened: "17:20". */
export const momentTime = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: COMPANY_TZ,
  })
}

/**
 * The office day a moment belongs to, as `2026-09-25`, for grouping.
 *
 * The Inbox draws a rule between days by comparing the first ten characters of the timestamp,
 * which is a UTC day: everything that happened after 5pm was filed under tomorrow, so an afternoon
 * of work was cut in half by a line saying the wrong date.
 */
export const momentDay = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-CA', { timeZone: COMPANY_TZ })
}

/**
 * THE DATES WE HAVE, NOT ONLY THE PAIR.
 *
 * This returned nothing unless BOTH dates were known, which was right while every lead came from
 * a channel that gives both or neither. The website form gives a move-in date and a LENGTH BAND —
 * so `check_out` is empty by design, and the screen said "not given" about a lead whose row holds
 * `check_in = 2026-11-01`. We knew when she was moving in and told the agent we did not.
 *
 * Found by opening the product and looking at a real enquiry, which is the only way this kind of
 * defect is ever found: nothing was wrong, nothing went red, a fact was just invisible.
 */
export const dateRange = (a, b) => {
  const x = shortDate(a)
  const y = shortDate(b)
  if (x && y) return `${x} – ${y}`
  if (x) return `from ${x}`
  if (y) return `until ${y}`
  return null
}
